import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import {
    Link,
    makeStyles,
    Theme, Typography,
} from '@material-ui/core';

import { Page } from '../../components/Page';
import { TextField } from '../../components/TextField';
import { Button } from '../../components/Button';
import { FormGroup } from '../../components/FormGroup';
import { PageBody } from '../../components/PageBody';
import { api } from '../../api';
import { useLoading } from '../../units/loading';
import { StatusFalseError } from '../../api/error/StatusFalseError';
import { useAlert } from '../../units/alert';
import { ResetPasswordRequestData } from '../../api/resetPassword';
import { useQuery } from '../../units/useQuery';
import { Logo } from '../../components/Logo';

type RecoverPasswordPageProps = {};

RecoverPasswordPage.defaultProps = {};

const useStyles = makeStyles((theme: Theme) => ({
    root: {

    },
    title: {
        margin: theme.spacing(2, 1),
    },
}));

function RecoverPasswordPage(props: RecoverPasswordPageProps): React.ReactElement {
    const classes = useStyles();
    const history = useHistory();
    const { setIsLoading } = useLoading();
    const alert = useAlert();
    const query = useQuery();
    const [confirmPassword, setConfirmPassword] = useState('');

    const [formData, setFormData] = useState<ResetPasswordRequestData>({
        type: '2',
        login: '',
        password: '',
        verify_code: '',
    });

    useEffect(() => {
        const verifyCode = query.get('verify_code');
        const login = query.get('login');
        if (typeof verifyCode === 'string' && typeof login === 'string') {
            setIsLoading(true);
            api.verifyCodeCheck({
                login,
                verify_code: verifyCode,
            }).then(() => {
                setIsLoading(false);
                setFormData({
                    type: '2',
                    login,
                    password: formData.password,
                    verify_code: verifyCode,
                });
            }).catch(async (e) => {
                setIsLoading(false);
                if (e instanceof StatusFalseError) {
                    const errorData = await e.response.json();
                    alert.show({
                        type: 'error',
                        title: '錯誤',
                        message: errorData.msg,
                    });
                }
            });
        }
    }, []);

    async function handleSubmitClick() {
        setIsLoading(true);
        try {
            await api.verifyCodeCheck({
                login: formData.login,
                verify_code: formData.verify_code || '',
            });
        } catch (e) {
            setIsLoading(false);
            if (e instanceof StatusFalseError) {
                alert.show({
                    type: 'error',
                    title: '錯誤',
                    message: '此連結已失效',
                    onConfirm: () => history.replace('/login'),
                });
            }

            return;
        }

        api.verifyCodeCheck({
            login: formData.login,
            verify_code: formData.verify_code || '',
        })
            .then(() => api.resetPassword(formData))
            .then(() => {
                setIsLoading(false);
                alert.show({
                    type: 'success',
                    title: '操作成功',
                    message: '已成功重設密碼，請以新密碼登入',
                    onConfirm: () => {
                        history.replace('/login');
                    },
                });
            })
            .catch(async (e) => {
                setIsLoading(false);
                if (e instanceof StatusFalseError) {
                    const errorData = await e.response.json();
                    alert.show({
                        type: 'error',
                        title: '錯誤',
                        message: errorData.msg,
                    });
                }
            });
    }

    function isConfirmPasswordPass() {
        return (confirmPassword && formData.password) && (confirmPassword === formData.password);
    }

    return (
        <Page className={classes.root}>
            <PageBody>
                <Logo />
                <Typography className={classes.title} variant="h5" align="center">重設密碼</Typography>
                <FormGroup>
                    <TextField
                        label="新密碼"
                        type="password"
                        value={formData.password}
                        onChange={(e) => setFormData({
                            ...formData,
                            password: e.target.value,
                        })}
                    />
                    <TextField
                        label="確認新密碼"
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        error={Boolean(confirmPassword && formData.password) && !isConfirmPasswordPass()}
                        helperText={Boolean(confirmPassword && formData.password) && !isConfirmPasswordPass() ? '密碼與確認新密碼不同' : ''}
                    />
                    <Button onClick={handleSubmitClick} disabled={!isConfirmPasswordPass()}>重設密碼</Button>
                </FormGroup>
            </PageBody>
        </Page>
    );
}

export { RecoverPasswordPage };
